/**
 * Premium /portfolio page content — proof metrics, featured cases, explorer, capabilities.
 */
import portfolioSnapshotSites from './portfolioSnapshotSites.json';
import legacyPortfolioProjects from './portfolioLegacyProjects';

export type PortfolioFilterId =
  | 'all'
  | 'education'
  | 'ecommerce'
  | 'marketplace'
  | 'ai'
  | 'platforms'
  | 'wordpress'
  | 'magento'
  | 'laravel'
  | 'react'
  | 'reactnative'
  | 'codeigniter';

export type PortfolioExplorerProject = {
  id: string;
  title: string;
  href: string;
  image: string;
  alt: string;
  stack: string | null;
  stackLabel: string | null;
  categories: PortfolioFilterId[];
  summary: string;
  services: string[];
};

export type FeaturedCaseStudy = {
  id: string;
  title: string;
  href: string;
  image: string;
  alt: string;
  supportingImages?: {
    src: string;
    alt: string;
  }[];
  category: string;
  stack: string;
  summary: string;
  proofPoints: string[];
  services: string[];
  layout: 'hero' | 'wide' | 'standard';
};

export type SnapshotSite = {
  url: string;
  image: string;
  group: 'education' | 'marketplace';
  title?: string;
};

export const SNAPSHOT_SITES = portfolioSnapshotSites as SnapshotSite[];

export const EDUCATION_SNAPSHOTS = SNAPSHOT_SITES.filter((s) => s.group === 'education');
export const MARKETPLACE_SNAPSHOTS = SNAPSHOT_SITES.filter((s) => s.group === 'marketplace');

export const PORTFOLIO_PROOF_METRICS = [
  {
    id: 'captures',
    value: String(SNAPSHOT_SITES.length),
    label: 'full-page captures',
    detail: 'Live desktop snapshots of education and marketplace properties we support worldwide.',
  },
  {
    id: 'industries',
    value: 'Multi-industry',
    label: 'delivery proof',
    detail: 'Education, ecommerce, B2B marketplaces, platforms, AI interfaces, and mobile apps.',
  },
  {
    id: 'stacks',
    value: 'Full-stack',
    label: 'technology breadth',
    detail: 'Magento, React, WordPress, Laravel, CodeIgniter, React Native, CMS, and integrations.',
  },
  {
    id: 'support',
    value: 'Long-term',
    label: 'client support',
    detail: 'Forms, CRM, Salesforce, analytics, optimization, fixes, and ongoing operations.',
  },
];

export const HERO_PROOF_NODES = [
  'Education',
  'Ecommerce',
  'Marketplace',
  'Platforms',
  'AI',
  'Mobile',
  'CRM',
  'Support',
];

export const PORTFOLIO_FILTERS: { id: PortfolioFilterId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'education', label: 'Education' },
  { id: 'ecommerce', label: 'Ecommerce' },
  { id: 'marketplace', label: 'Marketplace' },
  { id: 'ai', label: 'AI' },
  { id: 'platforms', label: 'Platforms' },
  { id: 'magento', label: 'Magento' },
  { id: 'react', label: 'React' },
  { id: 'reactnative', label: 'React Native' },
  { id: 'laravel', label: 'Laravel' },
  { id: 'wordpress', label: 'WordPress' },
  { id: 'codeigniter', label: 'CodeIgniter' },
];

const STACK_LABEL: Record<string, string> = {
  magento: 'Magento',
  react: 'React',
  reactnative: 'React Native',
  laravel: 'Laravel',
  wordpress: 'WordPress',
  codeigniter: 'CodeIgniter',
};

/** Enriched explorer metadata keyed by title for legacy grid items. */
const EXPLORER_ENRICHMENT: Record<
  string,
  { categories: PortfolioFilterId[]; summary: string; services: string[] }
> = {
  TRUEOPINION: {
    categories: ['platforms', 'laravel'],
    summary: 'Survey operations client panel with structured data views and role-friendly workflows.',
    services: ['Laravel', 'Dashboard', 'Platform UX'],
  },
  'AI chatbot': {
    categories: ['ai', 'codeigniter'],
    summary: 'Conversational client interface with guided flows and support-ready handoff patterns.',
    services: ['AI', 'Chatbot', 'APIs'],
  },
  'India Freelancer': {
    categories: ['marketplace', 'react'],
    summary: 'Marketplace-style platform for freelancer discovery and project engagement.',
    services: ['React', 'Marketplace', 'Frontend'],
  },
  'Palm Springs Car Storage': {
    categories: ['platforms', 'codeigniter'],
    summary: 'Operations-facing web platform for storage booking and customer workflows.',
    services: ['CodeIgniter', 'CMS', 'Integrations'],
  },
  'Cashew Protect': {
    categories: ['platforms', 'laravel'],
    summary: 'Brand website with product positioning, trust content, and conversion paths.',
    services: ['Laravel', 'CMS', 'SEO'],
  },
  Fashionzapp: {
    categories: ['ecommerce', 'magento'],
    summary: 'Ecommerce storefront with catalog UX and conversion-focused product flows.',
    services: ['Magento', 'Ecommerce', 'UX'],
  },
  'Cuffs N Lashes': {
    categories: ['ecommerce', 'magento'],
    summary: 'Cosmetics ecommerce build with product discovery and checkout experience.',
    services: ['Magento', 'Ecommerce', 'Frontend'],
  },
  Faberkart: {
    categories: ['ecommerce', 'magento'],
    summary: 'Retail ecommerce presence with category navigation and product merchandising.',
    services: ['Magento', 'Ecommerce', 'Integrations'],
  },
  'Dhenu Sudha': {
    categories: ['ecommerce', 'magento'],
    summary: 'Product-led ecommerce site for dairy brand storytelling and online sales.',
    services: ['Magento', 'Ecommerce', 'CMS'],
  },
  "Men's Fit Club": {
    categories: ['platforms', 'wordpress'],
    summary: 'Content and membership-focused WordPress site with conversion-oriented layout.',
    services: ['WordPress', 'CMS', 'SEO'],
  },
  'Tipster Challenge': {
    categories: ['platforms'],
    summary: 'Community platform for challenges, rankings, and member participation.',
    services: ['PHP', 'Community', 'UX'],
  },
  "Ella's Bubbles": {
    categories: ['ecommerce', 'wordpress'],
    summary: 'WordPress ecommerce presence for product storytelling and enquiry conversion.',
    services: ['WordPress', 'Ecommerce', 'CMS'],
  },
  'WeCharge — customer app': {
    categories: ['ecommerce', 'reactnative'],
    summary: 'Customer mobile app for ordering, account management, and service workflows.',
    services: ['React Native', 'Mobile', 'APIs'],
  },
  'WeCharge — delivery app': {
    categories: ['ecommerce', 'reactnative'],
    summary: 'Delivery driver app with route-friendly workflows and order handoff patterns.',
    services: ['React Native', 'Mobile', 'Operations'],
  },
  'Balaji Mariline': {
    categories: ['platforms', 'wordpress'],
    summary: 'Logistics brand website with service clarity and enquiry-focused content structure.',
    services: ['WordPress', 'CMS', 'SEO'],
  },
};

function slugify(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function buildExplorerProjects(): PortfolioExplorerProject[] {
  return legacyPortfolioProjects.map((project) => {
    const enrich = EXPLORER_ENRICHMENT[project.title];
    const stackCategories: PortfolioFilterId[] = project.stack
      ? ([project.stack] as PortfolioFilterId[])
      : [];
    const categories = enrich?.categories ?? stackCategories;

    return {
      id: slugify(project.title),
      title: project.title,
      href: project.href,
      image: project.image,
      alt: project.alt,
      stack: project.stack,
      stackLabel: project.stack ? STACK_LABEL[project.stack] ?? project.stack : null,
      categories: categories.length ? categories : (['platforms'] as PortfolioFilterId[]),
      summary: enrich?.summary ?? 'Production web project delivered with clear UX and reliable engineering.',
      services: enrich?.services ?? (project.stack ? [STACK_LABEL[project.stack] ?? project.stack] : ['Web']),
    };
  });
}

export const PORTFOLIO_EXPLORER_PROJECTS = buildExplorerProjects();

export const FEATURED_CASE_STUDIES: FeaturedCaseStudy[] = [
  {
    id: 'sae-education',
    title: 'SAE — multi-region education platform',
    href: 'https://www.sae.edu.au/',
    image: 'img/portfolio-sae-edu-au.png',
    alt: 'SAE education website screenshot for a multi-region digital platform',
    supportingImages: [
      {
        src: 'img/portfolio-acap-edu-au.png',
        alt: 'ACAP education website supporting screenshot',
      },
      {
        src: 'img/portfolio-wla-edu-au.png',
        alt: 'Women and Leadership Australia education website supporting screenshot',
      },
      {
        src: 'img/portfolio-usa-sae-edu.png',
        alt: 'SAE USA education website supporting screenshot',
      },
    ],
    category: 'Education',
    stack: 'CMS / Frontend',
    summary:
      'Education website experience supporting regional content, programme discovery, enquiry paths, and multi-site operations.',
    proofPoints: ['12 regional education captures', 'Forms & enquiry flows', 'Multi-region CMS support'],
    services: ['Education UX', 'CMS', 'Frontend', 'Integrations'],
    layout: 'hero',
  },
  {
    id: 'india-sellers',
    title: 'India Sellers — B2B marketplace',
    href: 'https://www.indiasellers.com/',
    image: 'img/portfolio-indiasellers-com.png',
    alt: 'India Sellers B2B marketplace website screenshot',
    supportingImages: [
      {
        src: 'img/indiafreelancer.jpg',
        alt: 'India Freelancer marketplace supporting screenshot',
      },
      {
        src: 'img/portfolio-evanik-ai.png',
        alt: 'Evanik ecommerce marketplace supporting screenshot',
      },
    ],
    category: 'Marketplace',
    stack: 'Platform / SEO',
    summary:
      'B2B marketplace for seller discovery, product categories, services, and lead generation workflows.',
    proofPoints: ['Seller & product discovery', 'Category architecture', 'Lead generation paths'],
    services: ['Marketplace', 'Catalogs', 'SEO', 'Frontend'],
    layout: 'wide',
  },
  {
    id: 'wecharge',
    title: 'WeCharge — ecommerce & mobile',
    href: 'https://www.wecharge.co.in/',
    image: 'img/wecharge-p-1.png',
    alt: 'WeCharge Fulling India ecommerce website screenshot',
    supportingImages: [
      {
        src: 'img/app-3.png',
        alt: 'WeCharge mobile app supporting screenshot',
      },
      {
        src: 'img/fashionzapp.png',
        alt: 'Fashionzapp ecommerce supporting screenshot',
      },
      {
        src: 'img/cuffsnlashes.jpg',
        alt: 'Cuffs N Lashes ecommerce supporting screenshot',
      },
    ],
    category: 'Ecommerce',
    stack: 'PHP / React Native',
    summary:
      'Product-led ecommerce presence with storefront flows plus customer and delivery mobile apps.',
    proofPoints: ['Storefront UX', 'Customer & driver apps', 'Order workflows'],
    services: ['Ecommerce', 'React Native', 'PHP', 'Integrations'],
    layout: 'standard',
  },
  {
    id: 'ai-chatbot',
    title: 'TOSALL — AI chatbot interface',
    href: 'https://web.kailashchandra.com/tosall_client/',
    image: 'img/tosall-p-1.png',
    alt: 'AI chatbot client interface screenshot',
    supportingImages: [
      {
        src: 'img/trueopinion-p-1.png',
        alt: 'TrueOpinion platform dashboard supporting screenshot',
      },
      {
        src: 'img/pscs-p-1.png',
        alt: 'Palm Springs Car Storage admin supporting screenshot',
      },
    ],
    category: 'AI',
    stack: 'CodeIgniter / APIs',
    summary:
      'Conversational client interface with guided flows, admin patterns, and support-ready handoff.',
    proofPoints: ['Guided conversation UX', 'Admin interface', 'API-backed flows'],
    services: ['AI', 'Chatbot', 'APIs', 'UX'],
    layout: 'standard',
  },
  {
    id: 'trueopinion',
    title: 'TrueOpinion — survey platform',
    href: 'https://surveys.authenticopinion.com/clientpanel',
    image: 'img/trueopinion-p-1.png',
    alt: 'TrueOpinion survey platform dashboard screenshot',
    supportingImages: [
      {
        src: 'img/tosall-p-1.png',
        alt: 'TOSALL admin platform supporting screenshot',
      },
      {
        src: 'img/cashew-p-1.png',
        alt: 'Cashew Protect web platform supporting screenshot',
      },
    ],
    category: 'Platform',
    stack: 'Laravel',
    summary:
      'Client panel for survey operations with structured data views and role-friendly workflows.',
    proofPoints: ['Dashboard UX', 'Survey operations', 'Client panel workflows'],
    services: ['Laravel', 'Platform', 'Dashboard'],
    layout: 'standard',
  },
];

export const CAPABILITY_PROOF = [
  {
    id: 'ux',
    title: 'UX / UI & responsive design',
    text: 'Conversion-focused layouts, education enquiry paths, ecommerce catalog UX, and accessible responsive interfaces.',
  },
  {
    id: 'cms',
    title: 'CMS & content architecture',
    text: 'WordPress, Roots Sage, multi-region education content, and maintainable content structures clients can operate.',
  },
  {
    id: 'ecommerce',
    title: 'Ecommerce & storefront engineering',
    text: 'Magento, WooCommerce, product discovery, checkout flows, and catalog operations for retail and B2B.',
  },
  {
    id: 'backend',
    title: 'API / backend & platform logic',
    text: 'Laravel, CodeIgniter, React backends, secure APIs, dashboards, and operational workflows.',
  },
  {
    id: 'crm',
    title: 'CRM, forms & integrations',
    text: 'Salesforce, FormAssembly, enquiry forms, payment connections, and CRM-ready data flows.',
  },
  {
    id: 'ai',
    title: 'AI, search & analytics',
    text: 'Chatbots, automation interfaces, education search patterns, GTM analytics, and measurable optimization.',
  },
  {
    id: 'mobile',
    title: 'Mobile apps & React Native',
    text: 'Customer and delivery apps with order workflows, account management, and production-ready mobile UX.',
  },
  {
    id: 'support',
    title: 'Performance, security & support',
    text: 'Core Web Vitals tuning, monitoring, fixes, roadmap work, and long-term technical partnership.',
  },
];

export function hostLabel(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

export function projectMatchesFilter(
  project: PortfolioExplorerProject,
  filterId: PortfolioFilterId,
): boolean {
  if (filterId === 'all') return true;
  return project.categories.includes(filterId);
}

export const CLASSIC_GALLERY = {
  to: '/portfolio/classic',
  label: 'Open classic technology gallery',
  description: 'Browse the tabbed lightbox gallery organized by Magento, PHP, Laravel, CodeIgniter, and WordPress.',
};
